/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Form, Button } from 'react-bootstrap';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { compose } from 'redux';

import FlashRoot from 'components/FlashMessage/FlashRoot';
import {
  flashMessages,
  reviewSettings as reviewSettingsDuck,
  modals
} from 'ducks';
import ConfirmChangePageModal from './ConfirmChangePageModal';
import './styles/ReviewPage.scss';

const errorMsg = 'There was an error saving the settings. If this persists please contact customer support';
const succMsg = 'Your settings have been saved';

const SaveButton = ({
  onClick,
  disabled
}) => (
  <Button
    className='review-settings-save-button'
    onClick={onClick}
    disabled={disabled}
  >
    Save Settings
  </Button>
);

SaveButton.propTypes = {
  /** onSubmit function */
  onClick: PropTypes.func,

  /** Should button be disabled */
  disabled: PropTypes.bool
};

SaveButton.defaultProps = {
  disabled: true,
  onClick: () => false
};

const ReviewPage = ({
  className,
  children,
  reviewSettings,
  header,
  headerHelperText,
  initialFormikValues,
  route,
  router,
  showFlash,
  showConfirmDelete,
  onClose,
  onConfirm,
  onSubmit,
  reloadDucks,
  markDirty,
  markPristine,
  confirmDelete,
  validate
}) => {
  const [confirmRoute, setConfirmRoute] = useState(null);

  /** This is here as a bandaid for a corner case bug where if a user deletes a field (which causes a delete
   *  to be sent to the backend) and then leaves the page to another Review Settings page, selecting 'OK'
   *  on the page change alert, and then returning to the page with a deleted entry, the deleted entry will still
   *  show because redux hasn't marked it as dirty. If the data is marked dirty when deleted, this causes a data
   *  reload, throwing away unsaved data.
   */
  useEffect(() => {
    reloadDucks();
  }, []);

  const beforeUnloadListener = useCallback(e => {
    e.preventDefault();
    const prompt = 'You have unsaved changes.';
    e.returnValue = prompt;
    return prompt;
  }, []);

  return (
    <React.Fragment>
      <FlashRoot />
      <Formik
        initialValues={initialFormikValues}
        enableReinitialize
        validate={validate}
        onSubmit={(values, formikContext) => {
          const { setSubmitting } = formikContext;
          onSubmit({ values, formikContext, initialFormikValues })
            .then(res => {
              if (!res || !res.skipFlash) {
                showFlash({ message: succMsg });
              }
            })
            .catch(() => showFlash({ message: errorMsg, type: 'danger' }))
            .then(() => {
              setSubmitting(false);
              reloadDucks();
            });
        }}
      >
        {props => {
          useEffect(() => {
            /**
             * If the data is unsaved, warn the user before allowing them to changing
             * the page
             */
            if (props.dirty) {
              window.addEventListener('beforeunload', beforeUnloadListener);
            } else {
              window.removeEventListener('beforeunload', beforeUnloadListener);
            }

            router.setRouteLeaveHook(route, nextLocation => {
              if (props.dirty) {
                if (confirmRoute) {
                  setConfirmRoute(null);
                } else {
                  setConfirmRoute(nextLocation.pathname + nextLocation.search);
                  return false;
                }
              }
              return true;
            });
          }, [props.dirty, confirmRoute]);

          useEffect(() => {
            if (props.dirty !== reviewSettings.dirty) {
              if (props.dirty) {
                markDirty();
              } else {
                markPristine();
              }
            }
          }, [props.dirty]);

          useEffect(() => {
            if (showConfirmDelete) {
              confirmDelete(
                'This field will be removed from this section if you proceed.',
                'Are you sure?',
                () => {
                  onConfirm(props)
                    .then(res => {
                      if (!res || !res.skipFlash) {
                        showFlash({ message: succMsg });
                      }
                    })
                    .catch(() => showFlash({ message: errorMsg, type: 'danger' }))
                    .then(() => props.setSubmitting(false));
                },
                onClose
              );
            }
          }, [showConfirmDelete]);

          return (
            <Form className={className}>
              <ConfirmChangePageModal
                show={confirmRoute !== null}
                handleClose={() => setConfirmRoute(null)}
                handleConfirm={() => router.push(confirmRoute)}
              />
              <SaveButton onClick={props.handleSubmit} disabled={!props.dirty || props.isSubmitting} />
              <div className='review-section'>
                <div className='review-section-header'>{header}</div>
                <div className='review-section-help-text'>{headerHelperText}</div>
                {children}
              </div>
              <SaveButton onClick={props.handleSubmit} disabled={!props.dirty || props.isSubmitting} />
            </Form>
          );
        }}
      </Formik>
    </React.Fragment>
  );
};

ReviewPage.propTypes = {

  /** Classname */
  className: PropTypes.string,

  /** Special children prop */
  children: PropTypes.node.isRequired,

  /** Page Header */
  header: PropTypes.string.isRequired,

  /** Page Header Helper Text */
  headerHelperText: PropTypes.string,

  /** Show confirmation modal prop */
  showConfirmDelete: PropTypes.bool,

  /** initialValues for the FormikForm */
  initialFormikValues: PropTypes.shape({}).isRequired,

  /** React router route */
  route: PropTypes.shape({}).isRequired,

  /** React withRouter router */
  router: PropTypes.shape({
    setRouteLeaveHook: PropTypes.func
  }).isRequired,

  /** showFlash Message Dispatch */
  showFlash: PropTypes.func.isRequired,

  /** handles deleting of a field */
  onConfirm: PropTypes.func,

  /** handles closing of confirm delete modal */
  onClose: PropTypes.func,

  /** onSubmit function */
  onSubmit: PropTypes.func.isRequired,

  /** Reloads the ducks by marking them dirty */
  reloadDucks: PropTypes.func.isRequired
};

ReviewPage.defaultProps = {
  className: '',
  headerHelperText: '',
  showConfirmDelete: false,
  onConfirm: () => false,
  onClose: () => false
};

const mapDispatchToProps = (dispatch) => ({
  showFlash: (props) => dispatch(flashMessages.actions.show(props)),
  markDirty: () => dispatch(reviewSettingsDuck.actions.markDirty()),
  markPristine: () => dispatch(reviewSettingsDuck.actions.markPristine()),
  confirmDelete: (message, title, onConfirm, onClose) => (
    dispatch(modals.actions.show('VerificationModal', {
      message,
      title,
      onConfirm,
      onClose: () => {
        dispatch(modals.actions.hide('VericicationModal'));
        onClose();
      }
    }))
  )
});

const mapStateToProps = ({ reviewSettings }) => ({
  reviewSettings
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter
)(ReviewPage);
